import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { App, Card, Descriptions, Dropdown, Modal, Select as AntSelect, Table, Tag } from 'antd';
import { ColumnsType } from 'antd/es/table';

import { ReactComponent as Plus } from '../../../assets/add.svg';
import { ReactComponent as Ellipsis } from '../../../assets/ellipsis.svg';
import Button from '../../../custom/button/button';
import { getAdminUsers, getRoles, disableAdminUser, addUserToRole, removeUserFromRole } from '../request';
import { getErrorMessage } from '../../../utils/getErrorMessage';
import AddAdminUser from './addAdminUser';

const AdminUsers = () => {
  const { notification, modal } = App.useApp();
  const queryClient = useQueryClient();

  const [openAdd, setOpenAdd] = useState(false);
  const [openView, setOpenView] = useState(false);
  const [openAssignRole, setOpenAssignRole] = useState(false);
  const [openRemoveRole, setOpenRemoveRole] = useState(false);
  const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null);
  const [assignRoleName, setAssignRoleName] = useState<string | undefined>(undefined);
  const [removeRoleName, setRemoveRoleName] = useState<string | undefined>(undefined);

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['get-admin-users'],
    queryFn: getAdminUsers,
  });

  const { data: rolesData, isLoading: rolesLoading } = useQuery({
    queryKey: ['get-all-roles'],
    queryFn: getRoles,
  });

  const disableMutation = useMutation({ mutationFn: disableAdminUser });
  const assignRoleMutation = useMutation({ mutationFn: addUserToRole });
  const removeRoleMutation = useMutation({ mutationFn: removeUserFromRole });

  const toggleAddModal = () => setOpenAdd((prev) => !prev);

  const handleView = (record: AdminUser) => {
    setSelectedUser(record);
    setOpenView(true);
  };

  const handleAssignRole = (record: AdminUser) => {
    setSelectedUser(record);
    setAssignRoleName(undefined);
    setOpenAssignRole(true);
  };

  const handleRemoveRole = (record: AdminUser) => {
    setSelectedUser(record);
    setRemoveRoleName(undefined);
    setOpenRemoveRole(true);
  };

  const handleDisable = (record: AdminUser) => {
    const isActive = record.isActive;
    modal.confirm({
      title: `${isActive ? 'Disable' : 'Enable'} Admin User`,
      content: `Are you sure you want to ${isActive ? 'disable' : 'enable'} ${record.firstName} ${record.lastName}?`,
      okText: isActive ? 'Disable' : 'Enable',
      okButtonProps: { danger: isActive },
      onOk: async () => {
        try {
          const res = await disableMutation.mutateAsync({ userId: record.applicantId, disable: isActive });
          notification.success({
            message: 'Success',
            description: res?.message || `User ${isActive ? 'disabled' : 'enabled'} successfully`,
          });
          queryClient.refetchQueries({ queryKey: ['get-admin-users'] });
          refetch();
        } catch (err: any) {
          notification.error({
            message: 'Error',
            description: err?.response?.data?.message || `Failed to ${isActive ? 'disable' : 'enable'} user`,
          });
        }
      },
    });
  };

  const handleAssignRoleSubmit = async () => {
    if (!selectedUser || !assignRoleName) return;
    try {
      const res = await assignRoleMutation.mutateAsync({
        userName: selectedUser.email,
        roleName: assignRoleName,
      });
      notification.success({
        message: 'Success',
        description: res?.message || 'Role assigned successfully',
      });
      queryClient.refetchQueries({ queryKey: ['get-admin-users'] });
      refetch();
      setOpenAssignRole(false);
    } catch (err: any) {
      notification.error({
        message: 'Error',
        description: err?.response?.data?.message || 'Failed to assign role',
      });
    }
  };

  const handleRemoveRoleSubmit = async () => {
    if (!selectedUser || !removeRoleName) return;
    try {
      const res = await removeRoleMutation.mutateAsync({
        userName: selectedUser.email,
        roleName: removeRoleName,
      });
      notification.success({
        message: 'Success',
        description: res?.message || 'Role removed successfully',
      });
      queryClient.refetchQueries({ queryKey: ['get-admin-users'] });
      refetch();
      setOpenRemoveRole(false);
    } catch (err: any) {
      notification.error({
        message: 'Error',
        description: err?.response?.data?.message || 'Failed to remove role',
      });
    }
  };

  const allRoleOptions = rolesData?.map((r) => ({ label: r.name, value: r.name })) || [];

  const currentRoleOptions = selectedUser?.roleList?.roles?.map((r) => ({ label: r, value: r })) || [];

  const columns: ColumnsType<AdminUser> = [
    {
      key: 'S/N',
      title: 'S/N',
      render: (_text, _record, index) => index + 1,
    },
    {
      key: 'firstName',
      title: 'First Name',
      dataIndex: 'firstName',
    },
    {
      key: 'lastName',
      title: 'Last Name',
      dataIndex: 'lastName',
    },
    {
      key: 'email',
      title: 'Email',
      dataIndex: 'email',
    },
    {
      key: 'phoneNo',
      title: 'Phone Number',
      dataIndex: 'phoneNumber',
    },
    {
      key: 'role',
      title: 'Role(s)',
      render: (_text, record) =>
        record?.roleList?.roles?.map((role) => <Tag key={role}>{role}</Tag>),
    },
    {
      key: 'isActive',
      title: 'Status',
      dataIndex: 'isActive',
      render: (isActive: boolean) => (
        <span style={{ color: isActive ? 'green' : 'red' }}>
          {isActive ? 'Active' : 'Disabled'}
        </span>
      ),
    },
    {
      key: 'action',
      title: 'Action',
      render: (_, record) => (
        <Dropdown
          menu={{
            items: [
              {
                label: 'View',
                key: 'view',
                onClick: () => handleView(record),
              },
              {
                label: 'Assign Role',
                key: 'assign-role',
                onClick: () => handleAssignRole(record),
              },
              {
                label: 'Remove Role',
                key: 'remove-role',
                onClick: () => handleRemoveRole(record),
              },
              {
                label: record.isActive ? 'Disable User' : 'Enable User',
                key: 'disable',
                danger: record.isActive,
                onClick: () => handleDisable(record),
              },
            ],
          }}
          trigger={['click']}
        >
          <Ellipsis />
        </Dropdown>
      ),
    },
  ];

  const userData = data as AdminUser[];

  return (
    <div>
      <section className="space-between">
        <h3>Admin Users</h3>
        <Button text="Add User" iconBefore={<Plus />} onClick={toggleAddModal} />
      </section>

      <br />

      <Card bordered={false}>
        <Table
          dataSource={userData}
          columns={columns}
          rowKey={(record) => record.applicantId}
          scroll={{ x: true }}
          loading={isLoading}
          pagination={false}
          locale={{ emptyText: isError ? getErrorMessage(error) : 'No admin users found' }}
        />
      </Card>

      {/* Add User */}
      <Modal title="Add Admin User" open={openAdd} onCancel={toggleAddModal} footer={null}>
        <AddAdminUser handleClose={toggleAddModal} refetch={refetch} />
      </Modal>

      {/* View User */}
      <Modal
        title="Admin User Details"
        open={openView}
        onCancel={() => setOpenView(false)}
        footer={null}
      >
        {selectedUser && (
          <Descriptions column={1} bordered size="small">
            <Descriptions.Item label="First Name">{selectedUser.firstName}</Descriptions.Item>
            <Descriptions.Item label="Last Name">{selectedUser.lastName}</Descriptions.Item>
            <Descriptions.Item label="Email">{selectedUser.email}</Descriptions.Item>
            <Descriptions.Item label="Phone Number">{selectedUser.phoneNumber}</Descriptions.Item>
            <Descriptions.Item label="Institution">{selectedUser.institutionShortName}</Descriptions.Item>
            <Descriptions.Item label="Role(s)">
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {selectedUser.roleList?.roles?.length ? (
                  selectedUser.roleList.roles.map((role) => <Tag key={role}>{role}</Tag>)
                ) : (
                  <span style={{ color: '#999' }}>No roles assigned</span>
                )}
              </div>
            </Descriptions.Item>
            <Descriptions.Item label="Status">
              <span style={{ color: selectedUser.isActive ? 'green' : 'red' }}>
                {selectedUser.isActive ? 'Active' : 'Disabled'}
              </span>
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>

      {/* Assign Role */}
      <Modal
        title={`Assign Role — ${selectedUser?.firstName} ${selectedUser?.lastName}`}
        open={openAssignRole}
        onCancel={() => setOpenAssignRole(false)}
        footer={null}
      >
        <div className="fields">
          <div>
            <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>Select Role</label>
            <AntSelect
              placeholder="Select a role to assign"
              size="large"
              style={{ width: '100%' }}
              options={allRoleOptions}
              value={assignRoleName}
              onChange={setAssignRoleName}
              loading={rolesLoading}
              showSearch
              filterOption={(input, option) =>
                (option?.label as string)?.toLowerCase().includes(input.toLowerCase())
              }
            />
          </div>
          <div className="btn-group" style={{ marginTop: 16 }}>
            <Button onClick={() => setOpenAssignRole(false)} variant="text" text="Cancel" />
            <Button
              text={assignRoleMutation.isPending ? 'Assigning...' : 'Assign Role'}
              onClick={handleAssignRoleSubmit}
              disabled={!assignRoleName || assignRoleMutation.isPending}
              isLoading={assignRoleMutation.isPending}
            />
          </div>
        </div>
      </Modal>

      {/* Remove Role */}
      <Modal
        title={`Remove Role — ${selectedUser?.firstName} ${selectedUser?.lastName}`}
        open={openRemoveRole}
        onCancel={() => setOpenRemoveRole(false)}
        footer={null}
      >
        <div className="fields">
          <div>
            <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>Select Role to Remove</label>
            <AntSelect
              placeholder="Select a role to remove"
              size="large"
              style={{ width: '100%' }}
              options={currentRoleOptions}
              value={removeRoleName}
              onChange={setRemoveRoleName}
            />
            {currentRoleOptions.length === 0 && (
              <p style={{ color: '#999', marginTop: 8, fontSize: 13 }}>
                This user has no roles assigned.
              </p>
            )}
          </div>
          <div className="btn-group" style={{ marginTop: 16 }}>
            <Button onClick={() => setOpenRemoveRole(false)} variant="text" text="Cancel" />
            <Button
              text={removeRoleMutation.isPending ? 'Removing...' : 'Remove Role'}
              onClick={handleRemoveRoleSubmit}
              disabled={!removeRoleName || removeRoleMutation.isPending}
              isLoading={removeRoleMutation.isPending}
            />
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default AdminUsers;
